import { useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Upload, Download, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import type { BackendUserRole, BulkImportResult, BulkImportRow } from "@/lib/platform-api";
import { importFailureMessage } from "@/lib/import-error";

const TEMPLATE_HEADER = "name,email,phone,department,roleType,roleLabel";
const TEMPLATE_SAMPLE = [
  TEMPLATE_HEADER,
  "Siti Aminah,siti@example.com,+60111111111,HR,HR_MANAGER,HR Manager",
  "Ahmad Faiz,ahmad@example.com,,Operations,HR_MANAGER,",
  "Priya Raj,priya@example.com,,Field,EMPLOYEE,",
].join("\n");

const VALID_ROLES: BackendUserRole[] = [
  "TENANT_OWNER",
  "HR_MANAGER",
  "SITE_ADMIN",
  "OPERATIONS_ADMIN",
  "EMPLOYEE",
];

type ParsedRow = BulkImportRow & { _lineNumber: number; _error?: string };

function parseCsv(text: string): ParsedRow[] {
  const lines = text.trim().split(/\r?\n/).filter((l) => l.trim() !== "");
  if (lines.length < 2) return [];
  const header = lines[0].split(",").map((s) => s.trim().toLowerCase());
  const idx = (name: string) => header.indexOf(name);
  const iName = idx("name");
  const iEmail = idx("email");
  const iPhone = idx("phone");
  const iDept = idx("department");
  const iRole = idx("roletype");
  const iLabel = idx("rolelabel");

  return lines.slice(1).map((line, i) => {
    const cols = line.split(",").map((s) => s.trim());
    const row: ParsedRow = {
      _lineNumber: i + 2,
      name: iName >= 0 ? cols[iName] ?? "" : "",
      email: iEmail >= 0 ? cols[iEmail] ?? "" : "",
      phone: iPhone >= 0 ? cols[iPhone] || undefined : undefined,
      department: iDept >= 0 ? cols[iDept] || undefined : undefined,
      roleType: (iRole >= 0 ? cols[iRole] : "EMPLOYEE").toUpperCase() as BackendUserRole,
      roleLabel: iLabel >= 0 ? cols[iLabel] || undefined : undefined,
    };
    if (!row.name) row._error = "Missing name";
    else if (!row.email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(row.email)) row._error = "Invalid email";
    // Retired (BR-208): superiors see their direct reports in My team.
    else if (row.roleType === "SUPERVISOR") row._error = "Supervisor is retired — superiors see their team in My team";
    else if (!VALID_ROLES.includes(row.roleType)) row._error = `Invalid roleType '${row.roleType}'`;
    return row;
  });
}

export interface BulkUserImportDialogProps {
  onImport: (rows: BulkImportRow[]) => Promise<BulkImportResult>;
  onCompleted?: () => void;
  title?: string;
  description?: string;
  trigger?: React.ReactNode;
}

export function BulkUserImportDialog({
  onImport,
  onCompleted,
  title = "Bulk import users",
  description = "Paste CSV rows to create many users at once. Each user receives an email invitation to set up their account.",
  trigger,
}: BulkUserImportDialogProps) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<BulkImportResult | null>(null);

  const parsed = useMemo(() => parseCsv(text), [text]);
  const validRows = parsed.filter((r) => !r._error);
  const invalidRows = parsed.filter((r) => r._error);

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_SAMPLE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "users-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const submit = async () => {
    if (validRows.length === 0) {
      toast.error("No valid rows to import");
      return;
    }
    setSubmitting(true);
    try {
      const res = await onImport(
        validRows.map(({ _lineNumber, _error, ...row }) => row),
      );
      setResult(res);
      toast.success(`Imported ${res.created.length} of ${res.totalProcessed} users`, {
        description: res.skipped.length > 0 ? `${res.skipped.length} skipped — see details` : undefined,
        duration: 8000,
      });
      onCompleted?.();
    } catch (e) {
      toast.error("Import failed", { description: importFailureMessage(e) });
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setText("");
    setResult(null);
  };

  const handleOpenChange = (o: boolean) => {
    setOpen(o);
    if (!o) reset();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button size="sm" variant="outline">
            <Upload className="mr-1.5 h-4 w-4" /> Bulk import
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="grid gap-3 py-2">
            <div className="flex items-center justify-between">
              <Label>CSV data</Label>
              <Button size="sm" variant="ghost" onClick={downloadTemplate} className="h-7 text-xs">
                <Download className="mr-1.5 h-3 w-3" /> Download template
              </Button>
            </div>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={TEMPLATE_SAMPLE}
              className="min-h-[200px] font-mono text-xs"
            />
            {parsed.length > 0 && (
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="outline" className="border-success/30 bg-success/10 text-success">
                  {validRows.length} valid
                </Badge>
                {invalidRows.length > 0 && (
                  <Badge variant="outline" className="border-destructive/30 bg-destructive/10 text-destructive">
                    {invalidRows.length} invalid
                  </Badge>
                )}
              </div>
            )}
            {invalidRows.length > 0 && (
              <div className="max-h-32 overflow-auto rounded border bg-muted/50 p-2 text-xs">
                {invalidRows.map((r) => (
                  <div key={r._lineNumber} className="flex items-start gap-2 py-0.5">
                    <AlertCircle className="h-3 w-3 shrink-0 text-destructive" />
                    <span className="text-muted-foreground">
                      Line {r._lineNumber}: <span className="text-destructive">{r._error}</span>
                      {r.email && <> — {r.email}</>}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className="text-[11px] text-muted-foreground">
              Required columns: name, email, roleType. Optional: phone, department, roleLabel.
              Valid roles: {VALID_ROLES.join(", ")}.
            </div>
          </div>
        )}

        {result && (
          <div className="grid gap-3 py-2">
            <div className="flex items-center gap-2 rounded-lg border bg-success/10 p-3 text-sm">
              <CheckCircle2 className="h-4 w-4 text-success" />
              <span className="font-medium">{result.created.length} users created</span>
            </div>
            {result.created.length > 0 && (
              <div className="max-h-56 overflow-auto rounded border p-2 text-xs">
                <div className="mb-1 text-[10px] font-medium uppercase text-muted-foreground">
                  Invitation emails sent
                </div>
                {result.created.map((u) => (
                  <div key={u.email} className="flex justify-between border-b py-1 last:border-b-0">
                    <span className="font-medium">{u.name}</span>
                    <span className="font-mono text-muted-foreground">{u.email}</span>
                  </div>
                ))}
              </div>
            )}
            {result.skipped.length > 0 && (
              <div className="max-h-40 overflow-auto rounded border border-destructive/30 bg-destructive/5 p-2 text-xs">
                <div className="mb-1 text-[10px] font-medium uppercase text-destructive">
                  {result.skipped.length} skipped
                </div>
                {result.skipped.map((s, i) => (
                  <div key={i} className="flex justify-between py-0.5">
                    <span className="text-muted-foreground">{s.email}</span>
                    <span className="text-destructive">{s.reason}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button onClick={() => handleOpenChange(false)}>Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={submitting}>Cancel</Button>
              <Button onClick={submit} disabled={submitting || validRows.length === 0}>
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Import {validRows.length > 0 && `(${validRows.length})`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
